import { useState, useEffect, useCallback, useRef } from 'react'
import { campaignsApi, type Campaign } from '../api/campaigns'
import { fetchMe } from '../api/auth'
import { leadProvidersApi, type LeadProvider } from '../api/leadProviders'
import { fetchAllProducts, type Product } from '../api/catalog'
import { CampaignOperatorsField } from '../components/CampaignOperatorsField'
import { CampaignCostImportDialog } from '../components/CampaignCostImportDialog'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import {
  Button, Callout, Card, Checkbox, DataTable, DataTableEmptyRow, Dialog, FilterBar, FormSection, IconButton, LoadingState, Page, PageHeader,
  Select, StatusPill, TextField, useConfirm, useSnackbar, type PillTone,
} from '../components/ui'

interface CampaignFormState {
  name: string
  description: string
  campaignType: string
  startDate: string
  endDate: string
  source: string
  // TASK-0197 (REQ-091-001): call-center calling script, free text — same non-optional local-state
  // treatment as description/source above.
  script: string
  // TASK-0207 (REQ-091-001): the campaign's stated objective, free text — same non-optional
  // local-state treatment as description/source/script above.
  objective: string
  // TASK-0217 (REQ-091-001): the campaign's merceological sector, free text — same non-optional
  // local-state treatment as description/source/script/objective above.
  sector: string
  // TASK-0218 (REQ-091-001): the campaign's product/package name, free text — same non-optional
  // local-state treatment as description/source/script/objective/sector above.
  product: string
  // TASK-0155 (REQ-104-001): manual-entry cost fields — kept as strings in form state (same pattern
  // as LeadFormPage.tsx's estimatedValue), parsed with parseFloat only on save.
  totalCost: string
  perLeadCost: string
  fixedCost: string
  variableCost: string
  // TASK-0206 (REQ-091-001): max contact attempts allowed per lead — kept as a string in form state,
  // same pattern as the cost fields above, parsed with parseInt only on save.
  maxAttempts: string
  // TASK-0224 (REQ-091-001): the daily calling window — "HH:mm" strings, the native value of an
  // `<input type="time">`, sent as-is on save (no parsing needed, unlike totalCost/maxAttempts).
  activityHoursStart: string
  activityHoursEnd: string
  // TASK-0228 (REQ-062-002): Ad Set/Creative level sub-campaign labels, free text — same
  // non-optional local-state treatment as description/source/script/objective/sector/product above.
  adSet: string
  creative: string
  // REQ-116-001: the LeadProvider that supplied this campaign — same `number | ''` "unset"
  // sentinel convention as ProductsPage.tsx's departmentId/categoryId/operatorId selects.
  leadProviderId: number | ''
  // F-143 (REQ-092-001): prodotto del catalogo ('' = nessuno), accanto al testo libero `product`.
  catalogProductId: number | ''
  // C-05: email automatiche al cliente per gli appuntamenti di questa campagna (spente di default).
  clientReminderEnabled: boolean
  noShowEmailEnabled: boolean
}

const EMPTY_FORM: CampaignFormState = {
  name: '',
  description: '',
  campaignType: '',
  startDate: '',
  endDate: '',
  source: '',
  script: '',
  objective: '',
  sector: '',
  product: '',
  totalCost: '',
  perLeadCost: '',
  fixedCost: '',
  variableCost: '',
  maxAttempts: '',
  activityHoursStart: '',
  activityHoursEnd: '',
  adSet: '',
  creative: '',
  leadProviderId: '',
  catalogProductId: '',
  clientReminderEnabled: false,
  noShowEmailEnabled: false,
}

function formatDate(s?: string): string {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// TASK-0068: domain vocabulary is active|paused|completed|archived (Campaign.cs), not
// draft|active|paused|ended|archived — "draft" was never reachable (Campaign.Create always sets
// "active", no draft-creation concept exists anywhere in this module) and the frontend previously
// sent "ended" where the domain actually stores "completed". Both are fixed here to match
// Campaign.ValidStatuses exactly, same vocabulary ContestsPage.tsx already uses for its own
// draft|active|completed|cancelled lifecycle.
const CAMPAIGN_STATUSES = ['active', 'paused', 'completed', 'archived'] as const

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { tone: PillTone; label: string }> = {
    active:    { tone: 'success', label: 'Attiva' },
    paused:    { tone: 'warning', label: 'In pausa' },
    completed: { tone: 'info', label: 'Completata' },
    archived:  { tone: 'neutral', label: 'Archiviata' },
  }
  const entry = map[status] ?? { tone: 'neutral' as PillTone, label: status }
  return <StatusPill tone={entry.tone}>{entry.label}</StatusPill>
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Attiva', paused: 'In pausa', completed: 'Completata', archived: 'Archiviata',
}

function campaignTypeLabel(t?: string): string {
  if (!t) return '—'
  const map: Record<string, string> = {
    email: 'Email',
    sms: 'SMS',
    social: 'Social',
    call: 'Chiamata',
    other: 'Altro',
  }
  return map[t] ?? t
}

// F-137: creare, modificare e importare i costi delle campagne e' riservato al personale interno (EqualStaff lato server).
const STAFF_ROLES = [
  'Administrator', 'EqualAdmin', 'EqualDirettoreGenerale', 'EqualDirettoreCommerciale',
  'EqualAreaManager', 'EqualOperatore', 'EqualBackOffice',
]

export function CampaignsPage() {
  const snackbar = useSnackbar()
  const confirm = useConfirm()
  const [canWrite, setCanWrite] = useState(false)
  useEffect(() => {
    let alive = true
    fetchMe().then(me => { if (alive) setCanWrite(!!me && STAFF_ROLES.includes(me.role)) }).catch(() => {})
    return () => { alive = false }
  }, [])
  const [items, setItems] = useState<Campaign[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  // F-118: import dei costi da file.
  const [showCostImport, setShowCostImport] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null)
  const [form, setForm] = useState<CampaignFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  // REQ-116-001: active lead providers for the form's picker below — small, mostly static
  // reference data, fetched once, same pattern as ProductsPage.tsx's departments effect.
  const [leadProviders, setLeadProviders] = useState<LeadProvider[]>([])
  // F-143 (REQ-092-001): prodotti del catalogo per il selettore (dato di riferimento, letto una volta).
  const [catalogProducts, setCatalogProducts] = useState<Product[]>([])
  useEffect(() => {
    fetchAllProducts().then(r => setCatalogProducts(r.items)).catch(() => setCatalogProducts([]))
  }, [])

  // Guards against openEdit's GET resolving out of order: if the user clicks
  // "Modifica" on one row and then another before the first request lands, a
  // slower first request must not clobber the modal/form state that the
  // second (later, current) click already opened — see review of TASK-0055.
  const editRequestRef = useRef(0)

  const pageSize = 25

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const p: { page: number; search?: string; status?: string } = { page }
      if (search) p.search = search
      if (statusFilter) p.status = statusFilter
      const res = await campaignsApi.list(p)
      setItems(res.items)
      setTotal(res.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter, page])

  useEffect(() => { void load() }, [load])

  // F-096: dopo "Clona" la copia (paused, inattiva) compare nell'elenco e si apre in modifica.
  const openAfterClone = useOpenAfterClone(items, c => { void openEdit(c) })

  // REQ-116-001: fetched once — leadProvidersApi.list() already filters to active providers and
  // orders by name (LeadProvidersController.List), exactly what this dropdown needs.
  useEffect(() => {
    leadProvidersApi.list().then(setLeadProviders).catch(() => {})
  }, [])

  function openCreate() {
    setForm(EMPTY_FORM)
    setEditId(null)
    setSaveError('')
    setShowModal(true)
  }

  async function openEdit(c: Campaign) {
    const requestId = ++editRequestRef.current
    setEditLoadingId(c.id)
    setError('')
    try {
      // Load the real current values (description/source are omitted from the
      // list projection) so an unrelated field edit never wipes them — see
      // TASK-0055.
      const detail = await campaignsApi.get(c.id)
      // A newer openEdit() call (another row clicked, or the same row clicked
      // again) has since started — this response is stale. Applying it now
      // would silently overwrite the form/editId a later, current request
      // already populated (or is about to), including any in-progress edits
      // the user has already started typing. Drop it.
      if (editRequestRef.current !== requestId) return
      setForm({
        name: detail.name,
        description: detail.description ?? '',
        campaignType: detail.campaignType ?? '',
        startDate: detail.startDate ? detail.startDate.slice(0, 10) : '',
        endDate: detail.endDate ? detail.endDate.slice(0, 10) : '',
        source: detail.source ?? '',
        script: detail.script ?? '',
        objective: detail.objective ?? '',
        sector: detail.sector ?? '',
        product: detail.product ?? '',
        totalCost: detail.totalCost != null ? String(detail.totalCost) : '',
        perLeadCost: detail.perLeadCost != null ? String(detail.perLeadCost) : '',
        fixedCost: detail.fixedCost != null ? String(detail.fixedCost) : '',
        variableCost: detail.variableCost != null ? String(detail.variableCost) : '',
        maxAttempts: detail.maxAttempts != null ? String(detail.maxAttempts) : '',
        // TASK-0224 (REQ-091-001): API returns "HH:mm:ss" — slice to "HH:mm" for the native
        // <input type="time"> value.
        activityHoursStart: detail.activityHoursStart ? detail.activityHoursStart.slice(0, 5) : '',
        activityHoursEnd: detail.activityHoursEnd ? detail.activityHoursEnd.slice(0, 5) : '',
        adSet: detail.adSet ?? '',
        creative: detail.creative ?? '',
        leadProviderId: detail.leadProviderId ?? '',
        catalogProductId: detail.catalogProductId ?? '',
        clientReminderEnabled: detail.clientReminderEnabled ?? false,
        noShowEmailEnabled: detail.noShowEmailEnabled ?? false,
      })
      setEditId(c.id)
      setSaveError('')
      setShowModal(true)
    } catch (e) {
      if (editRequestRef.current !== requestId) return
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento della campagna')
    } finally {
      // Only the most recent request may clear the loading indicator — an
      // earlier, now-stale request finishing later must not stomp the
      // loading state of whichever request is actually current.
      if (editRequestRef.current === requestId) setEditLoadingId(null)
    }
  }

  function closeModal() {
    setShowModal(false)
    setEditId(null)
    setSaveError('')
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setSaveError('Il nome è obbligatorio')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      const payload: {
        name: string; description?: string; campaignType?: string; startDate?: string; endDate?: string; source?: string
        script?: string
        objective?: string
        sector?: string
        product?: string
        totalCost?: number; perLeadCost?: number; fixedCost?: number; variableCost?: number
        maxAttempts?: number
        activityHoursStart?: string
        activityHoursEnd?: string
        adSet?: string
        creative?: string
        leadProviderId?: number
        catalogProductId?: number
        clientReminderEnabled?: boolean
        noShowEmailEnabled?: boolean
      } = { name: form.name }
      if (form.description) payload.description = form.description
      if (form.campaignType) payload.campaignType = form.campaignType
      if (form.startDate) payload.startDate = form.startDate
      if (form.endDate) payload.endDate = form.endDate
      if (form.source) payload.source = form.source
      if (form.script) payload.script = form.script
      if (form.objective) payload.objective = form.objective
      if (form.sector) payload.sector = form.sector
      if (form.product) payload.product = form.product
      if (form.totalCost !== '') payload.totalCost = parseFloat(form.totalCost)
      if (form.perLeadCost !== '') payload.perLeadCost = parseFloat(form.perLeadCost)
      if (form.fixedCost !== '') payload.fixedCost = parseFloat(form.fixedCost)
      if (form.variableCost !== '') payload.variableCost = parseFloat(form.variableCost)
      if (form.maxAttempts !== '') payload.maxAttempts = parseInt(form.maxAttempts, 10)
      if (form.activityHoursStart) payload.activityHoursStart = form.activityHoursStart
      if (form.activityHoursEnd) payload.activityHoursEnd = form.activityHoursEnd
      if (form.adSet) payload.adSet = form.adSet
      if (form.creative) payload.creative = form.creative
      if (form.leadProviderId !== '') payload.leadProviderId = Number(form.leadProviderId)
      if (form.catalogProductId !== '') payload.catalogProductId = Number(form.catalogProductId)
      payload.clientReminderEnabled = form.clientReminderEnabled
      payload.noShowEmailEnabled = form.noShowEmailEnabled
      if (editId !== null) {
        await campaignsApi.update(editId, payload)
      } else {
        await campaignsApi.create(payload)
      }
      closeModal()
      void load()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  // TASK-0068: reuses ContestsPage.tsx's handleSetStatus pattern (api/performance.ts's
  // contestsApi.updateStatus → PATCH .../status), adapted to a single per-row <select> (see the
  // "Stato" column below) rather than per-transition buttons — Campaign's 4-value vocabulary has
  // no enforced ordering/state-machine (any value → any other value is a valid PATCH, same as
  // Contest.SetStatus), so a plain select covers every transition with less branching than
  // conditionally-rendered buttons, while staying just as functional.
  async function handleSetStatus(c: Campaign, status: string) {
    if (status === c.status) return
    try {
      await campaignsApi.updateStatus(c.id, status)
      void load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore durante il cambio di stato', tone: 'error' })
    }
  }

  async function handleDelete(c: Campaign) {
    const ok = await confirm({
      title: 'Archiviare la campagna',
      message: `Archiviare la campagna "${c.name}"? Se ha lead collegati resta in archivio (i lead restano intatti); se è vuota viene rimossa.`,
      confirmLabel: 'Archivia',
      destructive: true,
    })
    if (!ok) return
    try {
      const res = await campaignsApi.delete(c.id)
      if (res?.archived) snackbar.show({ message: 'Campagna archiviata: ha lead collegati, quindi non è stata cancellata. I lead restano intatti.' })
      void load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : "Errore durante l'eliminazione", tone: 'error' })
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <Page>
      <PageHeader
        title="Campagne"
        icon="campaign"
        {...(total > 0 ? { count: total } : {})}
        actions={
          <>
            {canWrite && (
              <Button type="button" variant="outlined" icon="upload_file" onClick={() => setShowCostImport(true)}>
                Importa costi
              </Button>
            )}
            {canWrite && (
              <Button type="button" variant="filled" icon="add" onClick={openCreate}>
                Nuova campagna
              </Button>
            )}
          </>
        }
      />

      <FilterBar>
        <TextField
          label="Cerca campagna"
          leadingIcon="search"
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
        />
        <Select
          label="Stato"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
        >
          <option value="">Tutti gli stati</option>
          {CAMPAIGN_STATUSES.map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </Select>
      </FilterBar>

      {error && <Callout tone="error" role="alert">{error}</Callout>}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : (
          <DataTable variant="plain" stickyHeader>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Stato</th>
                <th>Data inizio</th>
                <th>Data fine</th>
                <th>Data creazione</th>
                <th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <DataTableEmptyRow colSpan={7} icon="campaign" title="Nessuna campagna trovata" />
              ) : items.map(c => (
                <tr key={c.id}>
                  <td className="md-typescale-emphasized-body-medium">{c.name}</td>
                  <td className="text-muted">{campaignTypeLabel(c.campaignType)}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <StatusBadge status={c.status} />
                      <Select
                        label="Cambia stato"
                        labelHidden
                        value={c.status}
                        title="Cambia stato"
                        onChange={e => void handleSetStatus(c, e.target.value)}
                      >
                        {CAMPAIGN_STATUSES.map(s => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </Select>
                    </div>
                  </td>
                  <td className="text-muted">{formatDate(c.startDate)}</td>
                  <td className="text-muted">{formatDate(c.endDate)}</td>
                  <td className="text-muted">{formatDate(c.createdAt)}</td>
                  <td className="text-end">
                    <CloneConfigurationButton
                      entityLabel="campagna"
                      url={`/api/campaigns/${c.id}/clone`}
                      defaultName={c.name}
                      note="Si copiano la configurazione e gli operatori. Non si copiano lead, costi e storico. La copia nasce in pausa e inattiva."
                      onCloned={async id => {
                        setStatusFilter(''); setSearch(''); setPage(1)
                        await load()
                        openAfterClone(id)
                      }}
                    />
                    {canWrite && (
                      <IconButton
                        size="sm"
                        icon={editLoadingId === c.id ? 'progress_activity' : 'edit'}
                        aria-label="Modifica"
                        disabled={editLoadingId === c.id}
                        onClick={() => void openEdit(c)}
                      />
                    )}
                    {canWrite && (
                      <IconButton
                        size="sm"
                        icon="delete"
                        aria-label="Archivia"
                        onClick={() => void handleDelete(c)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-between p-2">
            <span className="text-muted md-typescale-body-small">
              Pagina {page} di {totalPages}
            </span>
            <div className="d-flex gap-2">
              <Button
                type="button"
                variant="outlined"
                size="sm"
                icon="chevron_left"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Precedente
              </Button>
              <Button
                type="button"
                variant="outlined"
                size="sm"
                trailingIcon="chevron_right"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Successiva
              </Button>
            </div>
          </div>
        )}
      </Card>

      {showCostImport && (
        <CampaignCostImportDialog
          onClose={() => setShowCostImport(false)}
          onApplied={() => { void load() }}
        />
      )}

      {/* Create/Edit Modal */}
      <Dialog
        as="form"
        onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica campagna' : 'Nuova campagna'}
        actions={
          <>
            <Button type="button" variant="text" onClick={closeModal}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        {saveError && <Callout tone="error">{saveError}</Callout>}
        <FormSection title="Campagna">
          <TextField className="md-span-full"
            label="Nome *"
            type="text"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <Select
            label="Tipo"
            value={form.campaignType}
            onChange={e => setForm(f => ({ ...f, campaignType: e.target.value }))}
          >
            <option value="">— Seleziona tipo —</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="social">Social</option>
            <option value="call">Chiamata</option>
            <option value="other">Altro</option>
          </Select>
          {/* TASK-0217 (REQ-091-001): merceological sector — free text, no vocabulary, short
              label (like Fonte, NOT a textarea like Script/Obiettivo). */}
          <TextField
            label="Settore"
            type="text"
            supportingText="es. Energia, Telefonia/Fibra..."
            value={form.sector}
            onChange={e => setForm(f => ({ ...f, sector: e.target.value }))}
          />
          {/* TASK-0218 (REQ-091-001): product/package name — free text, no vocabulary, short
              label (like Fonte/Settore, NOT a textarea like Script/Obiettivo). */}
          <TextField
            label="Prodotto"
            type="text"
            supportingText="es. Luce, Gas, Fibra 1Gbps..."
            value={form.product}
            onChange={e => setForm(f => ({ ...f, product: e.target.value }))}
          />
          {/* F-143 (REQ-092-001): prodotto del catalogo, per scegliere lo script per prodotto e operatore. */}
          <Select className="md-span-full"
            id="cf-catalogProduct"
            label="Prodotto di catalogo"
            supportingText="Collega la campagna a un prodotto: lo script può essere scelto per prodotto o per operatore."
            value={form.catalogProductId}
            onChange={e => setForm(f => ({ ...f, catalogProductId: e.target.value ? Number(e.target.value) : '' }))}
          >
            <option value="">Nessuno</option>
            {catalogProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          {/* F-088 (TASK-0342): operatori della lista, solo Admin (la sezione si nasconde con 403). */}
          {editId !== null && <CampaignOperatorsField campaignId={editId} />}
        </FormSection>
        <FormSection title="Periodo e budget">
          <TextField
            label="Data inizio"
            type="date"
            value={form.startDate}
            onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
          />
          <TextField
            label="Data fine"
            type="date"
            value={form.endDate}
            onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
          />
          {/* TASK-0224 (REQ-091-001): daily calling window, both optional independently — the
              backend rejects only a set-and-inverted pair (start >= end). */}
          <TextField
            id="cf-activityHoursStart"
            label="Orario attività dalle"
            type="time"
            value={form.activityHoursStart}
            onChange={e => setForm(f => ({ ...f, activityHoursStart: e.target.value }))}
          />
          <TextField
            id="cf-activityHoursEnd"
            label="alle"
            type="time"
            value={form.activityHoursEnd}
            onChange={e => setForm(f => ({ ...f, activityHoursEnd: e.target.value }))}
          />
          {/* TASK-0206 (REQ-091-001): max contact attempts allowed per lead in this campaign. */}
          <TextField
            className="md-span-full"
            id="cf-maxAttempts"
            label="Tentativi Massimi"
            type="number"
            min="0"
            value={form.maxAttempts}
            onChange={e => setForm(f => ({ ...f, maxAttempts: e.target.value }))}
          />
          {/* TASK-0155 (REQ-104-001): manual-entry cost fields — import is out of scope. */}
          <TextField
            id="cf-totalCost"
            label="Costo totale €"
            type="number"
            min="0"
            step="0.01"
            value={form.totalCost}
            onChange={e => setForm(f => ({ ...f, totalCost: e.target.value }))}
          />
          <TextField
            id="cf-perLeadCost"
            label="Costo per lead €"
            type="number"
            min="0"
            step="0.01"
            value={form.perLeadCost}
            onChange={e => setForm(f => ({ ...f, perLeadCost: e.target.value }))}
          />
          <TextField
            id="cf-fixedCost"
            label="Costo fisso €"
            type="number"
            min="0"
            step="0.01"
            value={form.fixedCost}
            onChange={e => setForm(f => ({ ...f, fixedCost: e.target.value }))}
          />
          <TextField
            id="cf-variableCost"
            label="Costo variabile €"
            type="number"
            min="0"
            step="0.01"
            value={form.variableCost}
            onChange={e => setForm(f => ({ ...f, variableCost: e.target.value }))}
          />
        </FormSection>
        <FormSection title="Tracciamento">
          <TextField
            label="Fonte"
            type="text"
            supportingText="es. Google Ads, Meta..."
            value={form.source}
            onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
          />
          {/* REQ-116-001: links this campaign to the LeadProvider that supplied it — the
              first hop of the Provider -> Campaign -> Lead attribution chain. Options come
              from leadProvidersApi.list(), already filtered to active providers. */}
          <Select
            id="cf-leadProviderId"
            label="Fornitore Lead"
            value={form.leadProviderId}
            onChange={e => {
              const val = e.target.value
              setForm(f => ({ ...f, leadProviderId: val ? Number(val) : '' }))
            }}
          >
            <option value="">—</option>
            {leadProviders.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
          {/* TASK-0228 (REQ-062-002): Ad Set/Creative level sub-campaign labels — free text,
              no vocabulary, short labels (like Settore/Prodotto, NOT a textarea like
              Script/Obiettivo). The "configurable Lead Source master table" half of this
              requirement's residual is out of scope for this task. */}
          <TextField
            id="cf-adSet"
            label="Ad Set"
            type="text"
            supportingText="es. Imprenditori Milano..."
            value={form.adSet}
            onChange={e => setForm(f => ({ ...f, adSet: e.target.value }))}
          />
          <TextField
            id="cf-creative"
            label="Creative"
            type="text"
            supportingText="es. Video 03..."
            value={form.creative}
            onChange={e => setForm(f => ({ ...f, creative: e.target.value }))}
          />
        </FormSection>
        <FormSection title="Messaggi al cliente" columns={1}>
          {/* C-05 (REQ-085-001, REQ-086-001): partono solo se la funzione e' accesa dall'amministratore
              (impostazione "appuntamenti.promemoria_cliente") e il lead ha un indirizzo email. */}
          <Checkbox
            label="Promemoria via email 24 ore prima dell'appuntamento"
            checked={form.clientReminderEnabled}
            onChange={e => setForm(f => ({ ...f, clientReminderEnabled: e.target.checked }))}
          />
          <Checkbox
            label="Email dopo un appuntamento saltato (no-show)"
            checked={form.noShowEmailEnabled}
            onChange={e => setForm(f => ({ ...f, noShowEmailEnabled: e.target.checked }))}
          />
        </FormSection>
        <FormSection title="Note" columns={1}>
          {/* TASK-0197 (REQ-091-001): call-center calling script — free text, no vocabulary. */}
          <TextField
            label="Script"
            multiline
            rows={2}
            value={form.script}
            onChange={e => setForm(f => ({ ...f, script: e.target.value }))}
          />
          {/* TASK-0207 (REQ-091-001): campaign objective — free text, no vocabulary. */}
          <TextField
            label="Obiettivo"
            multiline
            rows={2}
            value={form.objective}
            onChange={e => setForm(f => ({ ...f, objective: e.target.value }))}
          />
        </FormSection>
      </Dialog>
    </Page>
  )
}
